/**
 * HYPSEYA — Doctor / Psychiatrist Dashboard
 * Clinical focus: PHQ-9, GAD-7, biometric indicators, ERI score.
 * No Pattern Intelligence narratives — structured clinical data only.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import ConfirmModal from '@/components/ConfirmModal';
import orbUrl from '../orbAsset';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ────────────────────────────────────────────────────────────────
function phq9Band(score) {
  if (score == null) return null;
  if (score <= 4)  return { label: 'Minimal',            color: '#1E6B68' };
  if (score <= 9)  return { label: 'Mild',               color: '#4AACA8' };
  if (score <= 14) return { label: 'Moderate',           color: '#B8906A' };
  if (score <= 19) return { label: 'Mod. Severe',        color: '#C07040' };
  return               { label: 'Severe',               color: '#B05040' };
}

function gad7Band(score) {
  if (score == null) return null;
  if (score <= 4)  return { label: 'Minimal',            color: '#1E6B68' };
  if (score <= 9)  return { label: 'Mild',               color: '#4AACA8' };
  if (score <= 14) return { label: 'Moderate',           color: '#B8906A' };
  return               { label: 'Severe',               color: '#B05040' };
}

function ERIBadge({ score }) {
  if (score == null) return <span style={{ fontSize: '0.75rem', color: '#C5D4D2' }}>—</span>;
  const color = score >= 80 ? '#1E6B68' : score >= 60 ? '#2D8B87' : score >= 40 ? '#4AACA8' : '#B8906A';
  const band  = score >= 80 ? 'Strong' : score >= 60 ? 'Growing' : score >= 40 ? 'Emerging' : 'Early';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', fontWeight: '700', color }}>{Math.round(score)}</span>
      <span style={{ fontSize: '0.7rem', background: `${color}18`, color, padding: '2px 8px', borderRadius: 10, fontWeight: '600' }}>{band}</span>
    </div>
  );
}

function ScalePill({ label, score, band }) {
  if (score == null) {
    return (
      <div style={{ flex: 1, background: 'rgba(247,243,236,0.8)', borderRadius: 12, padding: '10px 12px' }}>
        <div style={{ fontSize: '0.6rem', color: '#AAB8B6', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: '0.78rem', color: '#C5D4D2', fontStyle: 'italic' }}>Not completed</div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, background: `${band.color}0e`, border: `1px solid ${band.color}28`, borderRadius: 12, padding: '10px 12px' }}>
      <div style={{ fontSize: '0.6rem', color: '#AAB8B6', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: '1.25rem', fontWeight: '700', color: band.color, fontFamily: "'Playfair Display', serif" }}>{score}</span>
        <span style={{ fontSize: '0.68rem', fontWeight: '600', color: band.color }}>{band.label}</span>
      </div>
    </div>
  );
}

// ── Patient Card ───────────────────────────────────────────────────────────
function PatientCard({ patient, onRevoke }) {
  const isPending = patient.status === 'pending';
  const [loadingReport, setLoadingReport] = useState(false);

  const viewReport = async () => {
    setLoadingReport(true);
    try {
      const res = await axios.post(`${API}/share/report/${patient.patient_id}`);
      window.open(`/therapist-report/${res.data.token}`, '_blank');
    } catch {
      toast.error('Could not load clinical report');
    } finally {
      setLoadingReport(false);
    }
  };

  const phq9 = patient.phq9_score ?? null;
  const gad7 = patient.gad7_score ?? null;
  const phqBand = phq9Band(phq9);
  const gadBand = gad7Band(gad7);

  const lastActive = patient.sessions_last_7_days > 0
    ? `${patient.sessions_last_7_days} session(s) this week`
    : 'No sessions this week';

  return (
    <div style={{
      background: '#fff', borderRadius: 18, padding: '20px 22px', marginBottom: 10,
      boxShadow: '0 2px 14px rgba(30,107,104,0.06)',
      border: isPending ? '1px dashed rgba(184,211,207,0.5)' : '1px solid rgba(184,211,207,0.2)',
      opacity: isPending ? 0.75 : 1,
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(30,58,58,0.1), rgba(30,107,104,0.07))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1rem', fontWeight: '700', color: '#1E3A3A',
        }}>
          {patient.display_name?.charAt(0)?.toUpperCase() || patient.patient_email?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1E3A3A' }}>
              {isPending ? patient.patient_email : (patient.display_name || patient.patient_email)}
            </span>
            {isPending && (
              <span style={{ fontSize: '0.62rem', background: 'rgba(184,144,106,0.15)', color: '#B8906A', padding: '2px 8px', borderRadius: 10, fontWeight: '600' }}>Pending</span>
            )}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#AAB8B6' }}>
            {isPending ? 'Invite sent · awaiting acceptance' : patient.patient_email}
          </div>
        </div>
        {!isPending && <ERIBadge score={patient.eri_score} />}
      </div>

      {!isPending && (
        <>
          {/* PHQ-9 + GAD-7 */}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <ScalePill label="PHQ-9" score={phq9} band={phqBand} />
            <ScalePill label="GAD-7" score={gad7} band={gadBand} />
          </div>

          {/* Session stats + biometrics */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 8 }}>
            {[
              { label: 'Sessions',   value: patient.total_sessions ?? 0 },
              { label: 'This week',  value: patient.sessions_last_7_days ?? 0 },
              { label: 'HRV trend',  value: patient.hrv_trend != null ? (patient.hrv_trend > 0 ? `+${patient.hrv_trend}%` : `${patient.hrv_trend}%`) : '—' },
              { label: 'Sleep',      value: patient.sleep_quality != null ? `${Math.round(patient.sleep_quality)}%` : '—' },
            ].map(s => (
              <div key={s.label} style={{ background: 'rgba(247,243,236,0.8)', borderRadius: 10, padding: '7px 4px', textAlign: 'center' }}>
                <div style={{ fontSize: '0.58rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 2 }}>{s.label}</div>
                <div style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1E3A3A' }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 8, fontSize: '0.7rem', color: '#C5D4D2' }}>{lastActive}</div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        {!isPending && (
          <button onClick={viewReport} disabled={loadingReport} style={{
            flex: 1, padding: '10px',
            background: 'rgba(30,58,58,0.06)',
            border: '1px solid rgba(30,58,58,0.15)', borderRadius: 14,
            color: '#1E3A3A', fontSize: '0.8rem', fontWeight: '600',
            cursor: loadingReport ? 'not-allowed' : 'pointer', opacity: loadingReport ? 0.6 : 1,
          }}>{loadingReport ? 'Loading…' : 'Clinical report'}</button>
        )}
        <button onClick={() => onRevoke(patient.connection_id)} style={{
          padding: '10px 14px', background: 'transparent',
          border: '1px solid rgba(184,211,207,0.4)', borderRadius: 14,
          color: '#AAB8B6', fontSize: '0.78rem', cursor: 'pointer',
        }}>Remove</button>
      </div>
    </div>
  );
}

// ── Invite Modal ───────────────────────────────────────────────────────────
function InviteModal({ doctorId, onDone, onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const cfg = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.post(`${API}/therapist/invite-patient`, { therapist_id: doctorId, patient_email: email.trim() }, cfg);
      toast.success(`Invite sent to ${email}`);
      onDone();
    } catch {
      toast.error('Failed to send invite.');
    } finally { setSending(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 8000,
      background: 'rgba(10,22,22,0.75)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: '100%', maxWidth: 520,
        background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 100%)',
        borderRadius: '24px 24px 0 0', padding: '28px 24px 40px',
        animation: 'hyp-slide-up 0.35s ease',
      }}>
        <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.35rem', color: '#1E3A3A', margin: '0 0 6px' }}>Add Patient</h2>
        <p style={{ margin: '0 0 22px', fontSize: '0.82rem', color: '#8FA3A0', lineHeight: 1.6 }}>
          The patient will receive access to the protocol. Their clinical data will appear in your dashboard once they accept.
        </p>

        <label style={{ display: 'block', fontSize: '0.78rem', color: '#8FA3A0', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          Patient email
        </label>
        <input
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="patient@email.com"
          onKeyDown={e => e.key === 'Enter' && handleInvite()}
          style={{ width: '100%', padding: '14px 16px', marginBottom: 20, borderRadius: 14, border: '1px solid rgba(184,211,207,0.5)', background: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', color: '#1E3A3A', outline: 'none', boxSizing: 'border-box' }}
        />

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleInvite} disabled={!email.trim() || sending} style={{
            flex: 1, padding: '14px',
            background: email.trim() ? 'linear-gradient(135deg, #1E3A3A 0%, #1E6B68 100%)' : 'rgba(184,211,207,0.3)',
            color: email.trim() ? '#fff' : '#AAB8B6',
            border: 'none', borderRadius: 20, fontSize: '0.9rem', fontWeight: '600',
            cursor: email.trim() ? 'pointer' : 'default',
          }}>{sending ? 'Sending…' : 'Send invite →'}</button>
          <button onClick={onClose} style={{ padding: '14px 18px', background: 'transparent', border: '1px solid rgba(184,211,207,0.5)', borderRadius: 20, color: '#AAB8B6', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
      <style>{`@keyframes hyp-slide-up { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────
export default function DoctorDashboard() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [user, setUser] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { try { setUser(JSON.parse(u)); } catch {} }
    setTimeout(() => setIsVisible(true), 60);
  }, []);

  useEffect(() => { if (user?.user_id) loadPatients(); }, [user]);

  const authCfg = async () => {
    try { const token = await getAccessTokenSilently(); return { headers: { Authorization: `Bearer ${token}` } }; }
    catch { return {}; }
  };

  const loadPatients = async () => {
    setLoading(true);
    try {
      const cfg = await authCfg();
      const res = await axios.get(`${API}/therapist/patients/${user.user_id}`, cfg);
      setPatients(res.data);
    } catch {}
    setLoading(false);
  };

  const handleRevoke = (connectionId) => {
    setConfirmModal({
      title: 'Remove patient',
      message: 'Remove this patient from your clinical list? You will lose access to their data.',
      confirmLabel: 'Remove',
      destructive: true,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          const cfg = await authCfg();
          await axios.delete(`${API}/therapist/disconnect`, { ...cfg, params: { therapist_id: user.user_id, connection_id: connectionId } });
          toast.success('Patient removed.');
          setPatients(prev => prev.filter(p => p.connection_id !== connectionId));
        } catch { toast.error('Failed to remove patient.'); }
      },
    });
  };

  const activePatients = patients.filter(p => p.status === 'active');
  const pendingPatients = patients.filter(p => p.status === 'pending');

  // Compute avg PHQ-9 and GAD-7 across active patients that have scores
  const phq9Values = activePatients.map(p => p.phq9_score).filter(v => v != null);
  const gad7Values = activePatients.map(p => p.gad7_score).filter(v => v != null);
  const avgPHQ9 = phq9Values.length ? Math.round(phq9Values.reduce((a, b) => a + b, 0) / phq9Values.length) : null;
  const avgGAD7 = gad7Values.length ? Math.round(gad7Values.reduce((a, b) => a + b, 0) / gad7Values.length) : null;

  return (
    <>
    {confirmModal && <ConfirmModal {...confirmModal} onClose={() => setConfirmModal(null)} />}
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F2F5F5 0%, #E8EEEE 55%, rgba(184,211,207,0.12) 100%)',
      padding: '0 0 100px',
      opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
    }}>
      <div style={{ padding: '20px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(184,211,207,0.4)', borderRadius: 20, padding: '8px 16px', cursor: 'pointer', fontSize: '0.82rem', color: '#8FA3A0' }}>← Back</button>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 20px 0' }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            {/* Stethoscope icon */}
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'rgba(30,58,58,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="13.5" cy="13.5" r="2.5" stroke="#1E3A3A" strokeWidth="1.3"/>
                <path d="M4 3C4 3 2 3 2 6C2 9 5 10 5 10L5 13C5 15.2 6.8 17 9 17C11.2 17 13 15.2 13 13V11" stroke="#1E3A3A" strokeWidth="1.3" strokeLinecap="round"/>
                <path d="M4 3H7" stroke="#1E3A3A" strokeWidth="1.3" strokeLinecap="round"/>
                <circle cx="4" cy="3" r="1" fill="#1E3A3A"/>
                <circle cx="7" cy="3" r="1" fill="#1E3A3A"/>
              </svg>
            </div>
            <div>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', color: '#1E3A3A', margin: 0, fontWeight: '500', lineHeight: 1.2 }}>Clinical Monitor</h1>
              <p style={{ margin: 0, color: '#8FA3A0', fontSize: '0.8rem' }}>Medical supervision · HYPSEYA</p>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
          {[
            { label: 'Active patients', value: activePatients.length,              color: '#1E3A3A' },
            { label: 'Avg PHQ-9',       value: avgPHQ9 != null ? avgPHQ9 : '—',    color: avgPHQ9 != null ? (phq9Band(avgPHQ9)?.color ?? '#1E3A3A') : '#AAB8B6' },
            { label: 'Avg GAD-7',       value: avgGAD7 != null ? avgGAD7 : '—',    color: avgGAD7 != null ? (gad7Band(avgGAD7)?.color ?? '#1E3A3A') : '#AAB8B6' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 16, padding: '14px 16px', boxShadow: '0 2px 10px rgba(30,58,58,0.06)' }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.7rem', fontWeight: '700', color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '0.65rem', color: '#AAB8B6', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Add patient CTA */}
        <button onClick={() => setShowInvite(true)} style={{
          width: '100%', padding: '14px',
          background: 'linear-gradient(135deg, #1E3A3A 0%, #1E6B68 100%)',
          color: '#fff', border: 'none', borderRadius: 20, fontSize: '0.9rem', fontWeight: '600',
          cursor: 'pointer', boxShadow: '0 4px 16px rgba(30,58,58,0.22)', marginBottom: 24,
        }}>+ Add patient</button>

        {/* Patient list */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#AAB8B6' }}>Loading…</div>
        ) : patients.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 20, padding: '40px 24px', textAlign: 'center', boxShadow: '0 2px 16px rgba(30,58,58,0.06)' }}>
            <img src={orbUrl} alt="" style={{ width: 70, marginBottom: 16, opacity: 0.7 }} />
            <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#1E3A3A', margin: '0 0 8px', fontSize: '1.2rem' }}>No patients yet</h3>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#AAB8B6', lineHeight: 1.6 }}>
              Add a patient to begin<br />clinical monitoring.
            </p>
          </div>
        ) : (
          <>
            {activePatients.length > 0 && (
              <>
                <div style={{ fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: 10 }}>Active</div>
                {activePatients.map(p => <PatientCard key={p.connection_id} patient={p} onRevoke={handleRevoke} />)}
              </>
            )}
            {pendingPatients.length > 0 && (
              <>
                <div style={{ fontSize: '0.68rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600', marginBottom: 10, marginTop: 20 }}>Pending</div>
                {pendingPatients.map(p => <PatientCard key={p.connection_id} patient={p} onRevoke={handleRevoke} />)}
              </>
            )}
          </>
        )}

        {/* Clinical data note */}
        <div style={{ marginTop: 20, padding: '14px 18px', background: 'rgba(30,58,58,0.04)', border: '1px solid rgba(30,58,58,0.1)', borderRadius: 14, fontSize: '0.75rem', color: '#8FA3A0', lineHeight: 1.7 }}>
          <span style={{ fontWeight: '600', color: '#1E3A3A' }}>Clinical data scope: </span>
          ERI, PHQ-9, GAD-7, biometric indicators and session frequency only. Journal entries and personal reflections are never shared in compliance with GDPR special category data rules.{' '}
          <a href="/privacy" style={{ color: '#2D8B87', textDecoration: 'underline', textUnderlineOffset: '2px' }}>Privacy policy</a>
        </div>
      </div>

      {showInvite && user?.user_id && (
        <InviteModal doctorId={user.user_id} onDone={() => { setShowInvite(false); loadPatients(); }} onClose={() => setShowInvite(false)} />
      )}
    </div>
    </>
  );
}
